import React, { useCallback, useEffect, useState } from 'react';
import Dropzone from 'react-dropzone';
import { makeStyles } from '@material-ui/core/styles';
import { AiOutlineCloudUpload } from 'react-icons/ai';
import { BaseHelperText } from 'src/components/common/BaseTextField/BaseHelperText/BaseHelperText';
import { FilePreviews } from './FilePreviews';
import { TUploaderFile, TUploaderFiles } from './types';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    border: `dashed 2px ${theme.palette.primary.main}`,
    padding: 0,
    margin: 0,
    height: '180px',
    width: '100%',
  },
  inputField: {
    display: 'none',
  },
  uploadIcon: {
    color: theme.palette.primary.main,
  },
}));

type PUploadFileButton = {
  onChooseFile: (files: (File | string)[]) => void;
  accept?: string;
  multiple?: boolean;
  error?: string;
  helperText?: string;
  enablePreview?: boolean;
  files?: TUploaderFile | TUploaderFiles;
  disabled?: boolean;
  onDelete?: () => void;
};

const getDragAndDropBorderStyles = (params: {
  isDragActive: boolean;
  isDragAccept: boolean;
  isDragReject: boolean;
}) => {
  const { isDragActive, isDragAccept, isDragReject } = params;

  const activeStyle = {
    borderColor: '#2196f3',
  };

  const acceptStyle = {
    borderColor: '#00e676',
  };

  const rejectStyle = {
    borderColor: '#ff1744',
  };

  return {
    cursor: 'pointer',
    ...(isDragActive ? activeStyle : {}),
    ...(isDragAccept ? acceptStyle : {}),
    ...(isDragReject ? rejectStyle : {}),
  };
};

const MemoizedFilesPreview = React.memo(FilePreviews);

export const FilesUploader = React.forwardRef<any, PUploadFileButton>(
  (props, ref) => {
    const {
      onChooseFile,
      accept = '',
      multiple = true,
      disabled = false,
      error,
      helperText,
      enablePreview = true,
      files,
      onDelete,
      ...btnProps
    } = props;

    const classes = useStyles();

    const dynamicStyles = error ? { border: 'dashed 2px red' } : {};

    const [localFiles, setLocalFiles] = useState<TUploaderFile>([]);

    useEffect(() => {
      if (files) {
        setLocalFiles(Array.isArray(files) ? files : [files]);
      }
    }, [files]);

    const setAllFiles = useCallback(
      (acceptedFiles: (File | string)[]) => {
        // Todo: Check why 'multiple' prop is not working
        let specificFiles;
        if (multiple) {
          specificFiles = acceptedFiles;
        } else {
          specificFiles = acceptedFiles.length
            ? [acceptedFiles[0]]
            : acceptedFiles;
        }

        setLocalFiles(specificFiles);
        onChooseFile(specificFiles);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [multiple],
    );

    const handleDrop = (acceptedFiles: (File | string)[]) => {
      const newFiles = [...localFiles, ...Array.from(acceptedFiles)];
      setAllFiles(newFiles);
    };

    return (
      <>
        {disabled ? (
          <></>
        ) : (
          <Dropzone
            ref={ref}
            onDrop={handleDrop}
            accept={accept}
            multiple={multiple}>
            {({
              getRootProps,
              getInputProps,
              isDragActive,
              isDragAccept,
              isDragReject,
            }) => {
              return (
                <div
                  className={classes.root}
                  style={dynamicStyles}
                  {...getRootProps({
                    style: getDragAndDropBorderStyles({
                      isDragAccept,
                      isDragReject,
                      isDragActive,
                    }),
                  })}
                  {...btnProps}>
                  <input
                    type="file"
                    className={classes.inputField}
                    {...getInputProps()}
                  />
                  <>
                    {/* <div style={{ minWidth: '40px', minHeight: '40px' }}>
                      <AiOutlineCloudUpload
                        className={classes.uploadIcon}
                        size="30"
                      />
                    </div> */}
                    <div className="svg"><svg xmlns="http://www.w3.org/2000/svg" width="47" height="41" viewBox="0 0 47 41" fill="none">
                      <path d="M30.9458 3.77771C31.2999 3.77771 31.5874 4.06525 31.5874 4.41931C31.5874 4.77336 31.2999 5.06091 30.9458 5.06091C28.2926 5.06109 25.9024 6.77987 25.0464 9.27087L24.9683 9.51404C24.8776 9.81989 24.579 10.0053 24.271 9.96423L24.2632 9.974L24.1714 9.94568C23.8323 9.84453 23.6387 9.48826 23.7397 9.1488C24.6612 6.05237 27.5307 3.77789 30.9458 3.77771Z" fill="#4A9EDB" stroke="white" strokeWidth="0.3" />
                      <path d="M37.8229 28.4884H34.8757C34.6045 28.4884 34.3845 28.2684 34.3845 27.9972C34.3845 27.726 34.6045 27.506 34.8757 27.506H37.8229C41.8854 27.506 45.1909 24.2005 45.1909 20.138C45.1909 16.0754 41.8854 12.7699 37.8229 12.7699H37.752C37.6096 12.7699 37.4741 12.7082 37.3808 12.6005C37.2875 12.4928 37.2454 12.35 37.2657 12.2089C37.3096 11.903 37.3317 11.5956 37.3317 11.2963C37.3317 7.77549 34.4669 4.91068 30.946 4.91068C29.5763 4.91068 28.27 5.33873 27.1683 6.14885C26.9262 6.32674 26.5823 6.2478 26.4424 5.98149C23.3222 0.0400754 15.1726 -0.757796 10.9475 4.41072C9.16764 6.58813 8.4683 9.42061 9.02869 12.1812C9.09044 12.4861 8.85711 12.7703 8.54731 12.7703H8.35047C4.28792 12.7703 0.982451 16.0758 0.982451 20.1383C0.982451 24.2009 4.28792 27.5064 8.35047 27.5064H11.2976C11.5689 27.5064 11.7889 27.7263 11.7889 27.9976C11.7889 28.2688 11.5689 28.4888 11.2976 28.4888H8.35047C3.74612 28.4888 0 24.7427 0 20.1383C0 15.6631 3.53873 11.9987 7.96488 11.7966C7.54911 8.93254 8.34416 6.04357 10.1868 3.789C14.7104 -1.74508 23.3796 -1.12478 27.0443 5.04612C28.2134 4.31317 29.5511 3.92867 30.9459 3.92867C35.2116 3.92867 38.5918 7.55937 38.296 11.8012C42.6814 12.0472 46.1731 15.6922 46.1731 20.1379C46.1731 24.7427 42.427 28.4884 37.8227 28.4884L37.8229 28.4884Z" fill="#4A9EDB" />
                      <path d="M23.021 15.2892C29.8751 15.2892 35.4507 20.8657 35.4507 27.7198C35.4506 34.5739 29.8751 40.1495 23.021 40.1495C16.1669 40.1495 10.5904 34.574 10.5903 27.7198C10.5903 20.8657 16.1668 15.2892 23.021 15.2892ZM23.021 16.5724C16.8744 16.5724 11.8735 21.5733 11.8735 27.7198C11.8736 33.8664 16.8745 38.8673 23.021 38.8673C29.1675 38.8673 34.1684 33.8663 34.1685 27.7198C34.1685 21.5733 29.1675 16.5724 23.021 16.5724Z" fill="#4A9EDB" stroke="white" strokeWidth="0.3" />
                      <path d="M23.2454 22.8032C23.5267 22.8032 23.755 23.0317 23.7551 23.313V32.6636C23.755 32.9452 23.5266 33.1733 23.2454 33.1733C22.9642 33.1732 22.7357 32.9448 22.7356 32.6636V23.313C22.7357 23.0318 22.9641 22.8034 23.2454 22.8032Z" fill="#4A9EDB" stroke="#4A9EDB" strokeWidth="0.3" />
                      <path d="M22.8848 22.9535C23.0836 22.7544 23.4065 22.7548 23.6055 22.9535L26.4824 25.8304C26.6815 26.0295 26.6815 26.3521 26.4824 26.5511C26.3828 26.651 26.2519 26.7006 26.1221 26.7006C25.992 26.7005 25.8611 26.6505 25.7617 26.5511L23.2451 24.0345L20.7285 26.5511C20.5296 26.7503 20.2068 26.7501 20.0078 26.5511C19.809 26.3521 19.8088 26.0294 20.0078 25.8304L22.8848 22.9535Z" fill="#4A9EDB" stroke="#4A9EDB" strokeWidth="0.3" />
                    </svg></div>
                    <p style={{ fontSize: '14px', color: '#000', fontWeight: '600' }}>Upload file</p>
                  </>
                  {helperText && (
                    <BaseHelperText error style={{ marginTop: '-15px' }}>
                      {helperText}
                    </BaseHelperText>
                  )}
                </div>
              );
            }}
          </Dropzone>
        )}
        {enablePreview && (
          <MemoizedFilesPreview
            files={localFiles}
            onRemove={setAllFiles}
            onDelete={onDelete}
          />
        )}
      </>
    );
  },
);

FilesUploader.displayName = 'FilesUploader';
